import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { Pause, Ban, Sparkles, Check, CheckCircle, Loader, Facebook, Instagram, Twitter, Linkedin, Youtube, Music, Github, MessageCircle, Link as LinkIcon, User } from 'lucide-react';

const ShowcasePublic = () => {
  const { slug, pageSlug } = useParams();
  const [site, setSite] = useState(null);
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState({ name:'', email:'', message:''});
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/showcase/public/${slug}${pageSlug?`/${pageSlug}`:''}`);
      setSite(res.data.site);
      setPages(res.data.pages||[]);
      setPage(res.data.page||null);
      setSent(false);
      setError(null);
    } catch (e) { 
      console.error(e);
      // Gérer l'erreur d'abonnement
      if (e.response?.data?.error === 'subscription_required') {
        setError({
          type: 'subscription',
          message: e.response.data.message,
          siteTitle: e.response.data.site_title
        });
      } else {
        setError({ type: 'not_found', message: 'Site non disponible' });
      }
    }
    finally { setLoading(false); }
  }, [slug, pageSlug]);

  useEffect(() => { load(); }, [load]);

  const submitContact = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    try {
      await api.post(`/showcase/public/${slug}/contact`, contact);
      setSent(true);
      setContact({ name:'', email:'', message:''});
      setTimeout(() => setSent(false), 5000);
    } catch (e) { 
      alert('Erreur lors de l\'envoi du message');
      console.error(e); 
    } finally {
      setContactSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
  );

  if (error?.type === 'subscription') return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
      <div className="text-center max-w-md mx-auto px-6">
        <Pause className="w-16 h-16 mx-auto mb-4 text-amber-600" />
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Site temporairement indisponible</h1>
        <p className="text-gray-600 mb-6">
          Ce site vitrine n'est pas accessible actuellement.
        </p>
        <a 
          href="/"
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
        >
          Retour à l'accueil
        </a>
      </div>
    </div>
  );

  if (!site || !page) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
      <div className="text-center">
        <Ban className="w-10 h-10 mx-auto mb-2 text-gray-400" />
        <div className="text-gray-600 text-lg">Site introuvable</div>
      </div>
    </div>
  );

  const primaryColor = site?.primary_color || '#4f46e5';

  return (
    <div 
      className="min-h-screen bg-white"
      style={{
        '--primary': primaryColor,
        fontFamily: site?.font_family || 'Inter, sans-serif'
      }}
    >
      {/* Main Content - Full Width */}
      <main>
        {(page.blocks_json||[]).length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Sparkles className="w-12 h-12 mx-auto mb-2" />
            <div>Cette page est vide</div>
          </div>
        ) : (
          (page.blocks_json||[]).map((blk, idx) => (
            <BlockRenderer 
              key={idx} 
              block={blk} 
              contact={contact} 
              setContact={setContact} 
              submitContact={submitContact} 
              contactSubmitting={contactSubmitting} 
              sent={sent} 
              primaryColor={primaryColor}
              site={site}
              pages={pages}
              page={page}
            />
          ))
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-400">© {new Date().getFullYear()} {site.titre}</p>
        </div>
      </footer>
    </div>
  );
};

const BlockRenderer = ({ block, contact, setContact, submitContact, contactSubmitting, sent, primaryColor, site, pages, page }) => {
  const { type, data } = block;
  
  switch(type){
    case 'header':
      return <HeaderBlock data={data} site={site} pages={pages} page={page} />;

    case 'button':
      const btnStyles = {
        solid: { backgroundColor: data.bgColor || '#4f46e5', color: data.textColor || '#ffffff', border: 'none' },
        outline: { backgroundColor: 'transparent', color: data.bgColor || '#4f46e5', border: `2px solid ${data.bgColor || '#4f46e5'}` },
        ghost: { backgroundColor: 'transparent', color: data.bgColor || '#4f46e5', border: 'none' },
        gradient: { background: `linear-gradient(135deg, ${data.bgColor || '#4f46e5'}, ${data.bgColor2 || '#9333ea'})`, color: data.textColor || '#ffffff', border: 'none' }
      };
      const sizeClasses = { sm: 'px-4 py-2 text-sm', md: 'px-6 py-3 text-base', lg: 'px-8 py-4 text-lg', xl: 'px-10 py-5 text-xl' };
      return (
        <section className="py-8 px-6 flex justify-center">
          <a
            href={data.url || '#'}
            className={`inline-flex items-center gap-2 font-semibold ${sizeClasses[data.size || 'lg']} ${data.animated ? 'hover:scale-105 transition-transform' : ''} ${data.fullWidth ? 'w-full max-w-md justify-center' : ''} shadow-lg hover:shadow-xl`}
            style={{ ...btnStyles[data.style || 'solid'], borderRadius: `${data.borderRadius || 12}px` }}
          >
            {data.icon && <span>{data.icon}</span>}
            {data.text || 'Bouton'}
          </a>
        </section>
      );

    case 'hero':
      return (
        <section className={`py-20 px-6 text-center bg-gradient-to-br ${data.bgGradient || 'from-indigo-600 to-purple-700'}`}>
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: data.textColor || '#fff' }}>{data.title}</h1>
            <p className="text-xl opacity-90" style={{ color: data.textColor || '#fff' }}>{data.subtitle}</p>
          </div>
        </section>
      );
      
    case 'paragraph':
      return (
        <section className="py-10 px-6">
          <div className="max-w-3xl mx-auto">
            <p style={{ fontSize: `${data.fontSize || 18}px`, textAlign: data.textAlign || 'left' }} className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {data.text}
            </p>
          </div>
        </section>
      );
      
    case 'image':
      return data.url ? (
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <img src={data.url} alt={data.alt || ''} className={`w-full ${data.shadow ? 'shadow-xl' : ''}`} style={{ borderRadius: `${data.borderRadius || 12}px` }} />
          </div>
        </section>
      ) : null;

    case 'features':
      return (
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {(data.items || []).map((item, i) => (
              <div key={i} className="bg-white p-6 shadow-sm text-center hover:shadow-lg transition" style={{ borderRadius: '12px' }}>
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case 'stats':
      return (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {(data.items || []).map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold" style={{ color: primaryColor }}>{stat.value}</div>
                <div className="text-gray-600 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      );

    case 'pricing':
      return (
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {(data.plans || []).map((plan, i) => (
              <div key={i} className={`p-6 transition hover:scale-105 ${plan.highlighted ? 'bg-gradient-to-br from-indigo-600 to-purple-700 text-white ring-4 ring-indigo-300 scale-105' : 'bg-white shadow-lg'}`} style={{ borderRadius: '16px' }}>
                <div className={`text-lg font-semibold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>{plan.name}</div>
                <div className="mt-4 flex items-baseline">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className={`ml-1 ${plan.highlighted ? 'text-white/70' : 'text-gray-500'}`}>{plan.period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {(plan.features || []).map((f, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <Check className={`w-4 h-4 ${plan.highlighted ? 'text-green-300' : 'text-green-500'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href={plan.buttonUrl || '#'}
                  className={`block w-full mt-6 py-3 font-semibold text-center transition ${plan.highlighted ? 'bg-white text-indigo-600 hover:bg-gray-100' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                  style={{ borderRadius: '8px' }}
                >
                  {plan.buttonText || 'Choisir'}
                </a>
              </div>
            ))}
          </div>
        </section>
      );

    case 'gallery':
      const cols = data.columns || 3;
      return (
        <section className="py-12 px-6">
          <div className="max-w-6xl mx-auto grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {(data.images || []).map((img, i) => (
              <div key={i} className="aspect-square overflow-hidden bg-gray-100" style={{ borderRadius: '12px' }}>
                <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
              </div>
            ))}
          </div>
        </section>
      );

    case 'youtube':
      const getYoutubeId = (url) => {
        const match = url?.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
        return match ? match[1] : null;
      };
      const videoId = getYoutubeId(data.url);
      return videoId ? (
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden shadow-xl" style={{ aspectRatio: data.aspect || '16/9', borderRadius: '12px' }}>
              <iframe 
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </section>
      ) : null;

    case 'cta':
      return (
        <section className={`py-16 px-6 text-center bg-gradient-to-r ${data.bgGradient || 'from-green-500 to-emerald-600'}`}>
          <h2 className="text-3xl font-bold text-white mb-2">{data.title}</h2>
          <p className="text-white/80 mb-6">{data.subtitle}</p>
          <a href={data.buttonUrl || '#'} className="inline-block px-8 py-3 bg-white text-gray-900 font-semibold hover:bg-gray-100 transition hover:scale-105" style={{ borderRadius: '8px' }}>
            {data.buttonText}
          </a>
        </section>
      );

    case 'testimonial':
      return (
        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-5xl text-gray-300 mb-4">"</div>
            <p className="text-2xl text-gray-700 italic mb-6">{data.quote}</p>
            <div className="font-bold text-gray-900">{data.author}</div>
            <div className="text-gray-500">{data.role}</div>
          </div>
        </section>
      );

    case 'contact_form':
      return (
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{data.title}</h2>
            {sent ? (
              <div className="bg-green-50 border-2 border-green-500 p-6 text-center" style={{ borderRadius: '8px' }}>
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <div className="text-green-700 font-semibold">Message envoyé avec succès !</div>
              </div>
            ) : (
              <form onSubmit={submitContact} className="space-y-4">
                <input className="w-full px-4 py-3 border focus:ring-2 focus:ring-indigo-500 outline-none" style={{ borderRadius: '8px' }} placeholder="Nom" value={contact.name} onChange={e=>setContact({...contact,name:e.target.value})} required />
                <input type="email" className="w-full px-4 py-3 border focus:ring-2 focus:ring-indigo-500 outline-none" style={{ borderRadius: '8px' }} placeholder="Email" value={contact.email} onChange={e=>setContact({...contact,email:e.target.value})} required />
                <textarea className="w-full px-4 py-3 border focus:ring-2 focus:ring-indigo-500 outline-none" style={{ borderRadius: '8px' }} rows={4} placeholder="Message" value={contact.message} onChange={e=>setContact({...contact,message:e.target.value})} required />
<button disabled={contactSubmitting} className="w-full py-3 text-white font-semibold transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: primaryColor, borderRadius: '8px' }}>
                  {contactSubmitting ? <><Loader className="w-5 h-5 animate-spin" /> Envoi...</> : (data.buttonText || 'Envoyer')}
                </button>
              </form>
            )}
          </div>
        </section>
      );

    case 'divider':
      const spacing = { sm: 'py-4', md: 'py-8', lg: 'py-12' };
      return (
        <div className={`${spacing[data.spacing || 'md']} px-6`}>
          {data.style === 'line' && <hr className="max-w-4xl mx-auto border-gray-200" />}
          {data.style === 'dots' && (
            <div className="flex justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-300"></span>
              <span className="w-2 h-2 rounded-full bg-gray-300"></span>
              <span className="w-2 h-2 rounded-full bg-gray-300"></span>
            </div>
          )}
          {data.style === 'wave' && (
            <svg viewBox="0 0 1200 50" className="w-full max-w-4xl mx-auto text-gray-200" fill="currentColor">
              <path d="M0,25 Q300,0 600,25 T1200,25 V50 H0 Z" />
            </svg>
          )}
        </div>
      );

    case 'countdown':
      return <CountdownBlock data={data} />;

    case 'faq':
      return <FAQBlock data={data} />;

    case 'team':
      return (
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Notre équipe</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {(data.members || []).map((m, i) => (
                <div key={i} className="text-center group">
                  <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 overflow-hidden ring-4 ring-white shadow-lg group-hover:scale-110 transition">
                    {m.image ? <img src={m.image} alt={m.name} className="w-full h-full object-cover" /> : (
                      <div className="w-full h-full flex items-center justify-center text-white"><User className="w-10 h-10" /></div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{m.name}</h3>
                  <p style={{ color: primaryColor }} className="font-medium">{m.role}</p>
                  <p className="text-gray-500 text-sm mt-2">{m.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'logos':
      return (
        <section className="py-12 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            {data.title && <h3 className="text-center text-gray-500 mb-8">{data.title}</h3>}
            <div className="flex items-center justify-center gap-12 flex-wrap">
              {(data.logos || []).map((logo, i) => (
                <img key={i} src={logo} alt="" className={`h-12 object-contain ${data.grayscale ? 'grayscale hover:grayscale-0' : ''} transition opacity-60 hover:opacity-100`} />
              ))}
            </div>
          </div>
        </section>
      );

    case 'social':
      const SocialIcon = ({ platform }) => {
        const icons = {
          facebook: Facebook, instagram: Instagram, twitter: Twitter, linkedin: Linkedin,
          youtube: Youtube, tiktok: Music, github: Github, discord: MessageCircle
        };
        const Icon = icons[platform] || LinkIcon;
        return <Icon className="w-6 h-6" />;
      };
      return (
        <section className="py-8 px-6">
          <div className="flex justify-center gap-4">
            {(data.links || []).map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-gray-100 hover:bg-indigo-100 flex items-center justify-center text-gray-600 hover:text-indigo-600 transition hover:scale-110">
                <SocialIcon platform={link.platform} />
              </a>
            ))}
          </div>
        </section>
      );

    case 'map':
      return (
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto overflow-hidden shadow-lg" style={{ height: data.height || 400, borderRadius: '12px' }}>
            <iframe
              title="Map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(data.address || 'Paris')}&z=${data.zoom || 14}&output=embed`}
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </section>
      );

    case 'banner':
      return (
        <div 
          className={`py-3 px-6 text-center ${data.animated ? 'animate-pulse' : ''}`} 
          style={{ backgroundColor: data.bgColor || '#ef4444' }}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-4 flex-wrap">
            <span className="text-white font-medium">{data.text}</span>
            {data.buttonText && (
              <a href={data.buttonUrl || '#'} className="px-4 py-1 bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition" style={{ borderRadius: '9999px' }}>
                {data.buttonText}
              </a>
            )}
          </div>
        </div>
      );

    case 'timeline':
      return (
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Notre histoire</h2>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-indigo-500 to-purple-600" style={{ borderRadius: '9999px' }} />
              {(data.events || []).map((evt, i) => (
                <div key={i} className={`relative flex items-center mb-8 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-5/12 ${i % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="bg-white p-4 shadow-lg hover:shadow-xl transition" style={{ borderRadius: '12px' }}>
                      <div className="text-2xl font-bold" style={{ color: primaryColor }}>{evt.year}</div>
                      <div className="font-semibold text-gray-900">{evt.title}</div>
                      <div className="text-gray-500 text-sm">{evt.desc}</div>
                    </div>
                  </div>
                  <div className="w-2/12 flex justify-center">
                    <div className="w-4 h-4 ring-4 ring-white shadow" style={{ backgroundColor: primaryColor, borderRadius: '9999px' }} />
                  </div>
                  <div className="w-5/12" />
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'image_slider':
      return <ImageSlider data={data} />;

    case 'text_slider':
      return <TextSlider data={data} primaryColor={primaryColor} />;

    default:
      return null;
  }
};

// Composant Header responsive avec menu hamburger
const HeaderBlock = ({ data, site, pages, page }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  
  const bgColor = data.bgColor || '#ffffff';
  const textColor = data.textColor || '#1f2937';
  const ctaBgColor = data.ctaBgColor || '#4f46e5';
  
  return (
    <header 
      className={`${data.sticky ? 'sticky top-0 z-50' : ''}`}
      style={{ backgroundColor: bgColor, boxShadow: '0 2px 20px rgba(0,0,0,0.08)' }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href={`/site/${site?.slug || ''}`} className="flex items-center gap-3 group">
            {data.showLogo && site?.logo_url && (
              <img 
                src={site.logo_url} 
                alt="" 
                className="h-9 w-9 md:h-10 md:w-10 object-contain transition-transform group-hover:scale-110" 
                style={{ borderRadius: '10px' }} 
              />
            )}
            {data.showLogo && !site?.logo_url && (
              <div 
                className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 transition-transform group-hover:scale-110" 
                style={{ borderRadius: '10px' }}
              />
            )}
            <span 
              className="font-bold text-lg md:text-xl transition-colors" 
              style={{ color: textColor }}
            >
              {site?.titre || 'Site'}
            </span>
          </a>

          {/* Desktop Navigation */}
          {data.showNav && pages && pages.length > 1 && (
            <nav className="hidden md:flex items-center gap-1">
              {pages.map((p, i) => {
                const isActive = page?.id === p.id;
                return (
                  <a 
                    key={i} 
                    href={`/site/${site?.slug}/${p.slug}`}
                    className={`relative px-4 py-2 font-medium transition-all duration-200 ${isActive ? 'font-semibold' : 'opacity-70 hover:opacity-100'}`}
                    style={{ color: textColor, borderRadius: '8px' }}
                  >
                    {p.titre}
                    {isActive && (
                      <span 
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5" 
                        style={{ backgroundColor: ctaBgColor, borderRadius: '2px' }}
                      />
                    )}
                  </a>
                );
              })}
            </nav>
          )}

          {/* Desktop CTA + Mobile Menu Button */}
          <div className="flex items-center gap-3">
            {/* Desktop CTA */}
            {data.ctaText && (
              <a 
                href={data.ctaUrl || '#'}
                className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold transition-all duration-200 hover:opacity-90 hover:scale-105 hover:shadow-lg"
                style={{ backgroundColor: ctaBgColor, borderRadius: '10px' }}
              >
                {data.ctaText}
              </a>
            )}

            {/* Mobile Menu Button */}
            {((data.showNav && pages && pages.length > 1) || data.ctaText) && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden flex flex-col justify-center items-center w-10 h-10 transition-colors"
                style={{ borderRadius: '8px', backgroundColor: mobileMenuOpen ? `${textColor}10` : 'transparent' }}
                aria-label="Menu"
              >
                <span 
                  className={`w-5 h-0.5 transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-1' : ''}`}
                  style={{ backgroundColor: textColor }}
                />
                <span 
                  className={`w-5 h-0.5 my-1 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}
                  style={{ backgroundColor: textColor }}
                />
                <span 
                  className={`w-5 h-0.5 transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}
                  style={{ backgroundColor: textColor }}
                />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-96 pb-4' : 'max-h-0'}`}
        >
          {data.showNav && pages && pages.length > 1 && (
            <nav className="flex flex-col gap-1 pt-2 border-t" style={{ borderColor: `${textColor}15` }}>
              {pages.map((p, i) => {
                const isActive = page?.id === p.id;
                return (
                  <a 
                    key={i} 
                    href={`/site/${site?.slug}/${p.slug}`}
                    className={`px-4 py-3 font-medium transition-all ${isActive ? 'font-semibold' : 'opacity-70'}`}
                    style={{ 
                      color: textColor, 
                      borderRadius: '10px',
                      backgroundColor: isActive ? `${ctaBgColor}15` : 'transparent'
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {p.titre}
                  </a>
                );
              })}
            </nav>
          )}
          
          {/* Mobile CTA */}
          {data.ctaText && (
            <a 
              href={data.ctaUrl || '#'}
              className="flex items-center justify-center gap-2 mx-2 mt-3 px-5 py-3 text-white font-semibold transition-all hover:opacity-90"
              style={{ backgroundColor: ctaBgColor, borderRadius: '10px' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              {data.ctaText}
            </a>
          )}
        </div>
      </div>
    </header>
  );
};

// Composant Countdown avec state
const CountdownBlock = ({ data }) => {
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, mins: 0, secs: 0 });

  React.useEffect(() => {
    const calculate = () => {
      const target = new Date(data.targetDate).getTime();
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        mins: Math.floor((diff / (1000 * 60)) % 60),
        secs: Math.floor((diff / 1000) % 60)
      };
    };
    setTimeLeft(calculate());
    const timer = setInterval(() => setTimeLeft(calculate()), 1000);
    return () => clearInterval(timer);
  }, [data.targetDate]);

  return (
    <section className={`py-12 px-6 text-center bg-gradient-to-r ${data.bgGradient || 'from-red-500 to-orange-500'}`}>
      <h3 className="text-xl text-white/90 mb-4">{data.title}</h3>
      <div className="flex justify-center gap-4">
        {[['days', 'Jours'], ['hours', 'Heures'], ['mins', 'Min'], ['secs', 'Sec']].map(([key, label]) => (
          <div key={key} className="bg-white/20 backdrop-blur p-4 min-w-[80px]" style={{ borderRadius: '12px' }}>
            <div className="text-4xl font-bold text-white">{timeLeft[key]}</div>
            <div className="text-white/70 text-sm">{label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Composant FAQ avec accordéon
const FAQBlock = ({ data }) => {
  const [openIndex, setOpenIndex] = React.useState(null);

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Questions fréquentes</h2>
        <div className="space-y-4">
          {(data.items || []).map((item, i) => (
            <div key={i} className="bg-white shadow-sm overflow-hidden" style={{ borderRadius: '12px' }}>
              <button 
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full p-4 font-semibold text-gray-900 flex items-center justify-between text-left hover:bg-gray-50 transition"
              >
                <span>{item.q}</span>
                <span className={`text-indigo-500 text-xl transition-transform ${openIndex === i ? 'rotate-45' : ''}`}>+</span>
              </button>
              <div className={`overflow-hidden transition-all ${openIndex === i ? 'max-h-96 pb-4' : 'max-h-0'}`}>
                <div className="px-4 text-gray-600">{item.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Composant Image Slider
const ImageSlider = ({ data }) => {
  const [current, setCurrent] = React.useState(0);
  const slides = data.slides || [];
  
  React.useEffect(() => {
    if (!data.autoplay || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % slides.length);
    }, (data.interval || 5) * 1000);
    return () => clearInterval(timer);
  }, [data.autoplay, data.interval, slides.length]);

  const prev = () => setCurrent(c => (c - 1 + slides.length) % slides.length);
  const next = () => setCurrent(c => (c + 1) % slides.length);

  if (slides.length === 0) return null;

  return (
    <section className="relative overflow-hidden" style={{ height: data.height || 500 }}>
      {slides.map((slide, i) => (
        <div 
          key={i}
          className={`absolute inset-0 transition-all duration-700 ease-in-out ${
            i === current ? 'opacity-100 translate-x-0' : 
            i < current ? 'opacity-0 -translate-x-full' : 'opacity-0 translate-x-full'
          }`}
        >
          {slide.image ? (
            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600" />
          )}
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{slide.title}</h2>
            <p className="text-xl md:text-2xl opacity-90">{slide.subtitle}</p>
          </div>
        </div>
      ))}

      {data.showArrows !== false && slides.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 backdrop-blur flex items-center justify-center text-white text-2xl transition" style={{ borderRadius: '9999px' }}>‹</button>
          <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 backdrop-blur flex items-center justify-center text-white text-2xl transition" style={{ borderRadius: '9999px' }}>›</button>
        </>
      )}

      {data.showDots !== false && slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, i) => (
            <button 
              key={i} 
              onClick={() => setCurrent(i)}
              className={`w-3 h-3 rounded-full transition-all ${i === current ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

// Composant Text Slider
const TextSlider = ({ data, primaryColor }) => {
  const [current, setCurrent] = React.useState(0);
  const slides = data.slides || [];
  
  React.useEffect(() => {
    if (!data.autoplay || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % slides.length);
    }, (data.interval || 4) * 1000);
    return () => clearInterval(timer);
  }, [data.autoplay, data.interval, slides.length]);

  if (slides.length === 0) return null;

  if (data.style === 'cards') {
    return (
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {slides.map((slide, i) => (
                <div key={i} className="w-full flex-shrink-0 px-4">
                  <div className="bg-white shadow-xl p-8 text-center" style={{ borderRadius: '16px' }}>
                    <div className="text-6xl text-indigo-200 mb-4">"</div>
                    <p className="text-xl text-gray-700 italic mb-6">{slide.quote}</p>
                    <div className="flex items-center justify-center gap-4">
                      {slide.avatar ? (
                        <img src={slide.avatar} alt={slide.author} className="w-14 h-14 object-cover ring-4 ring-indigo-100" style={{ borderRadius: '9999px' }} />
                      ) : (
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold" style={{ borderRadius: '9999px' }}>
                          {slide.author?.charAt(0)}
                        </div>
                      )}
                      <div className="text-left">
                        <div className="font-bold text-gray-900">{slide.author}</div>
                        <div className="text-gray-500 text-sm">{slide.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {slides.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {slides.map((_, i) => (
                <button 
                  key={i} 
                  onClick={() => setCurrent(i)}
                  className={`w-3 h-3 rounded-full transition-all ${i === current ? 'w-8' : ''}`}
                  style={{ backgroundColor: i === current ? primaryColor : '#d1d5db' }}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Style minimal
  return (
    <section className="py-16 px-6">
      <div className="max-w-3xl mx-auto text-center relative min-h-[200px]">
        {slides.map((slide, i) => (
          <div 
            key={i}
            className={`absolute inset-0 transition-all duration-500 ${i === current ? 'opacity-100' : 'opacity-0'}`}
          >
            <div className="text-5xl text-gray-200 mb-4">"</div>
            <p className="text-2xl text-gray-700 italic mb-6">{slide.quote}</p>
            <div className="font-bold text-gray-900">{slide.author}</div>
            <div className="text-gray-500">{slide.role}</div>
          </div>
        ))}
        {slides.length > 1 && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, i) => (
              <button 
                key={i} 
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === current ? 'w-6' : ''}`}
                style={{ backgroundColor: i === current ? primaryColor : '#d1d5db' }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ShowcasePublic;
